import React, { useState, useRef, useEffect } from 'react';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const CustomLevelDropdown = ({ userId, levels = [], userOptions, setUserOptions }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const userEntry = userOptions[userId] || {};
  const selected = userEntry.level_ids || [];
  const isExternalSelected = userEntry.externalType === true;
  const isLevelSelected = userEntry.levelType === true;

  const allLevelValues = levels.map((lvl) => lvl.value);
  const areAllLevelsSelected = allLevelValues.every((lvl) => selected.includes(lvl));

  const handleToggle = () => setOpen(!open);

  const handleOutsideClick = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setOpen(false);
    }
  };

  // Track page view
  useEffect(() => {
    mixpanel.track('Custom Level Dropdown Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const updateSelection = (newSelection, externalType, levelType) => {
    setUserOptions((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        level_ids: newSelection,
        externalType,
        levelType
      }
    }));
  };

  const handleSelectAllLevels = () => {
    if (areAllLevelsSelected) {
      updateSelection([], isExternalSelected, false);
    } else {
      updateSelection(allLevelValues, isExternalSelected, true);
    }
  };

  const handleLevelToggle = (value) => {
    const newSelection = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];

    const hasSelectedLevels = newSelection.length > 0;
    updateSelection(newSelection, isExternalSelected, hasSelectedLevels);
  };

  const handleExternalToggle = () => {
    const newExternalState = !isExternalSelected;
    updateSelection(selected, newExternalState, selected.length > 0);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <label className="text-gray-600 mb-1 block">Common Spaces</label>
      <div
        onClick={handleToggle}
        className={`border border-gray-300 px-3 py-2 rounded cursor-pointer bg-white ${
          selected.length === 0 && !isExternalSelected
            ? 'text-gray-400'
            : 'text-gray-600 font-semibold'
        }`}>
        {selected.length === 0 && !isExternalSelected
          ? 'Select Common Spaces'
          : [
              isExternalSelected ? 'External' : null,
              ...selected.map((val) => {
                const level = levels.find((lvl) => lvl.value === val);
                return level ? level.label : val;
              })
            ]
              .filter(Boolean)
              .join(', ')}
      </div>

      {open && (
        <div className="absolute z-10 w-full bg-white border border-gray-300 rounded mt-1 max-h-64 overflow-y-auto shadow-lg">
          <div className="p-2">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={isExternalSelected} onChange={handleExternalToggle} />
              External
            </label>

            <hr className="my-2" />

            <button
              className="text-blue-500 text-sm mb-2 underline"
              onClick={handleSelectAllLevels}>
              {areAllLevelsSelected ? 'Deselect All Levels' : 'Select All Levels'}
            </button>

            {levels.map((lvl) => (
              <label key={lvl.value} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selected.includes(lvl.value)}
                  onChange={() => handleLevelToggle(lvl.value)}
                />
                {lvl.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomLevelDropdown;
